import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  Box,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { useMutation, useQuery } from '@tanstack/react-query'
import ExternalApi from 'api/faber/ExternalApi'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import NormalText from 'common/Typography/NormalText'
import { Field, Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'

const formikInitialValues = {
  cancellationReason: '',
  otherReason: ''
}

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

export const CancelExternalPlanForm = ({
  handleClose,
  orderID,
  userId
}: {
  handleClose: () => void
  orderID: string
  userId: string
}) => {
  const faberToken = OrbitSession.get('faberToken')
  const [dataUsed, setDataUsed] = useState<number>(0)
  const [cancellationEligibility, setCancellationEligibility] =
    useState<string>('Available')

  const { data: dataUsage } = useQuery(['getDataUsage'], async () =>
    OneTimePackApi.getUserPlanByPaymentId(userId, orderID, faberToken)
  )

  useEffect(() => {
    if (dataUsage) {
      const usage = dataUsage.data.data.dataUsage
      setDataUsed(usage)
      setCancellationEligibility(usage > 0.1 ? 'Not Available' : 'Available')
    }
  }, [dataUsage])

  const { mutate: cancelPurchase } = useMutation(
    ExternalApi.cancelExternalPurchase,
    {
      onSuccess: () => {
        toast.success('External order cancelled successfully')
        handleClose()
      },
      onError: () => {
        toast.error('External order cancellation failed')
      }
    }
  )

  const handleFormSubmit = (values: NewCatalogFormikProps) => {
    const allValues = {
      orderID: orderID,
      faberToken: faberToken,
      cancellationReason:
        values.cancellationReason === 'Others -- Charge 4 USD'
          ? values.otherReason
          : values.cancellationReason
    }
    cancelPurchase(allValues)
  }

  const handleCancellationReasonChange = (
    event: React.ChangeEvent<{ value: unknown }>,
    setFieldValue: any //eslint-disable-line @typescript-eslint/no-explicit-any
  ) => {
    const value = event.target.value as string
    setFieldValue('cancellationReason', value)
    if (value === 'Others -- Charge 4 USD') {
      setFieldValue('otherReason', '')
    }
  }

  return (
    <Formik initialValues={formikInitialValues} onSubmit={handleFormSubmit}>
      {({
        submitForm,
        handleChange,
        errors,
        touched,
        handleBlur,
        values,
        setFieldValue
      }) => (
        <Form>
          <Grid container rowSpacing={2}>
            <Grid item xs={12}>
              <NormalText isBold>
                Data usage: {dataUsed.toFixed(2)} GB
              </NormalText>
            </Grid>
            <Grid item xs={12}>
              <NormalText isBold>
                Cancellation Eligibility:{' '}
                <Typography
                  component='span'
                  sx={{
                    color:
                      cancellationEligibility === 'Available' ? 'green' : 'red'
                  }}
                >
                  {cancellationEligibility}
                </Typography>
              </NormalText>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Cancellation Reason</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <Field
                  as={Select}
                  variant='standard'
                  sx={{ width: '100%' }}
                  onChange={(e) =>
                    handleCancellationReasonChange(e, setFieldValue)
                  }
                  name='cancellationReason'
                  value={values.cancellationReason}
                >
                  <MenuItem value="Poor network connectivity - Don't charge 4USD">
                    Poor network connectivity - Don&apos;t charge 4USD
                  </MenuItem>
                  <MenuItem value="Unable to connect to a network - Don't charge 4USD">
                    Unable to connect to a network - Don&apos;t charge 4USD
                  </MenuItem>
                  <MenuItem value='Did not use the eSIM - Charge 4 USD'>
                    Did not use the eSIM - Charge 4 USD
                  </MenuItem>
                  <MenuItem value='Others -- Charge 4 USD'>
                    Others -- Charge 4 USD
                  </MenuItem>
                </Field>
                {values.cancellationReason === 'Others -- Charge 4 USD' && (
                  <StyledTextField
                    name='otherReason'
                    placeholder='Please specify...'
                    onChange={(e) => {
                      handleBlur(e)
                      handleChange(e)
                    }}
                    value={values.otherReason}
                    fullWidth
                    variant='outlined'
                    error={!!(errors.otherReason && touched.otherReason)}
                    helperText={
                      errors.otherReason && touched.otherReason
                        ? errors.otherReason
                        : null
                    }
                  />
                )}
              </Box>
            </Grid>
          </Grid>
          <Grid>
            <Box mt={3}>
              <Button onClick={submitForm}>Cancel Order</Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

interface NewCatalogFormikProps {
  cancellationReason: string
  otherReason: string
}

export default CancelExternalPlanForm
